/**
 * forge-tryon i18n strings.
 *
 * Supports 8 locales covering the primary EU/US eyewear markets.
 * Use the `t()` helper to look up a key for a given locale with English fallback.
 */

/** Supported locale identifiers (BCP 47). */
export type Locale = 'en' | 'de' | 'fr' | 'es' | 'it' | 'pt' | 'nl' | 'ro';

/** All translatable keys used in the widget UI. */
export interface Messages {
  'modal.close': string;
  'modal.switchCamera': string;
  'modal.snapshot': string;
  'modal.ariaLabel': string;

  'tracking.alignFace': string;
  'tracking.detected': string;
  'tracking.lost': string;
  'tracking.restoring': string;

  'mode.live': string;
  'mode.photo': string;

  'photo.uploadTitle': string;
  'photo.uploadHint': string;

  'snapshot.download': string;
  'snapshot.share': string;
  'snapshot.copyLink': string;

  'state.cameraBlocked.title': string;
  'state.cameraBlocked.message': string;

  'state.cameraDenied.title': string;
  'state.cameraDenied.message': string;
  'state.cameraDenied.retry': string;

  'state.cameraError.title': string;

  'state.startingCamera.title': string;
  'state.startingCamera.message': string;

  'product.catalog': string;

  'cart.addToCart': string;
  'cart.added': string;
}

const en: Messages = {
  'modal.close': 'Close try-on',
  'modal.switchCamera': 'Switch camera',
  'modal.snapshot': 'Take snapshot',
  'modal.ariaLabel': 'Virtual eyewear try-on',

  'tracking.alignFace': 'Align your face in the frame',
  'tracking.detected': 'Face detected',
  'tracking.lost': 'Face not detected — look at the camera',
  'tracking.restoring': 'Restoring…',

  'mode.live': 'Live',
  'mode.photo': 'Photo',

  'photo.uploadTitle': 'Upload a photo or take a selfie',
  'photo.uploadHint': 'JPG, PNG, or WebP',

  'snapshot.download': 'Download',
  'snapshot.share': 'Share',
  'snapshot.copyLink': 'Copy link',

  'state.cameraBlocked.title': 'Camera access blocked',
  'state.cameraBlocked.message': 'To use virtual try-on, allow camera access in your browser settings and reload the page.',

  'state.cameraDenied.title': 'Camera access denied',
  'state.cameraDenied.message': 'Virtual try-on requires camera access. Grant permission in your browser and try again.',
  'state.cameraDenied.retry': 'Retry',

  'state.cameraError.title': 'Camera error',

  'state.startingCamera.title': 'Starting camera\u2026',
  'state.startingCamera.message': 'Please allow camera access when prompted.',

  'product.catalog': 'Product catalog',

  'cart.addToCart': 'Add to Cart',
  'cart.added': 'Added!',
};

const de: Messages = {
  'modal.close': 'Anprobe schlie\u00dfen',
  'modal.switchCamera': 'Kamera wechseln',
  'modal.snapshot': 'Foto aufnehmen',
  'modal.ariaLabel': 'Virtuelle Brillenanprobe',

  'tracking.alignFace': 'Richten Sie Ihr Gesicht im Rahmen aus',
  'tracking.detected': 'Gesicht erkannt',
  'tracking.lost': 'Kein Gesicht erkannt \u2014 sehen Sie in die Kamera',
  'tracking.restoring': 'Wird wiederhergestellt\u2026',

  'mode.live': 'Live',
  'mode.photo': 'Foto',

  'photo.uploadTitle': 'Foto hochladen oder Selfie aufnehmen',
  'photo.uploadHint': 'JPG, PNG oder WebP',

  'snapshot.download': 'Herunterladen',
  'snapshot.share': 'Teilen',
  'snapshot.copyLink': 'Link kopieren',

  'state.cameraBlocked.title': 'Kamerazugriff blockiert',
  'state.cameraBlocked.message': 'Um die virtuelle Anprobe zu nutzen, erlauben Sie den Kamerazugriff in Ihren Browsereinstellungen und laden Sie die Seite neu.',

  'state.cameraDenied.title': 'Kamerazugriff verweigert',
  'state.cameraDenied.message': 'Die virtuelle Anprobe erfordert Kamerazugriff. Erteilen Sie die Berechtigung in Ihrem Browser und versuchen Sie es erneut.',
  'state.cameraDenied.retry': 'Erneut versuchen',

  'state.cameraError.title': 'Kamerafehler',

  'state.startingCamera.title': 'Kamera wird gestartet\u2026',
  'state.startingCamera.message': 'Bitte erlauben Sie den Kamerazugriff, wenn Sie dazu aufgefordert werden.',

  'product.catalog': 'Produktkatalog',

  'cart.addToCart': 'In den Warenkorb',
  'cart.added': 'Hinzugef\u00fcgt!',
};

const fr: Messages = {
  'modal.close': 'Fermer l\u2019essayage',
  'modal.switchCamera': 'Changer de cam\u00e9ra',
  'modal.snapshot': 'Prendre une photo',
  'modal.ariaLabel': 'Essayage virtuel de lunettes',

  'tracking.alignFace': 'Alignez votre visage dans le cadre',
  'tracking.detected': 'Visage d\u00e9tect\u00e9',
  'tracking.lost': 'Visage non d\u00e9tect\u00e9 \u2014 regardez la cam\u00e9ra',
  'tracking.restoring': 'Restauration\u2026',

  'mode.live': 'Direct',
  'mode.photo': 'Photo',

  'photo.uploadTitle': 'T\u00e9l\u00e9charger une photo ou prendre un selfie',
  'photo.uploadHint': 'JPG, PNG ou WebP',

  'snapshot.download': 'T\u00e9l\u00e9charger',
  'snapshot.share': 'Partager',
  'snapshot.copyLink': 'Copier le lien',

  'state.cameraBlocked.title': 'Acc\u00e8s cam\u00e9ra bloqu\u00e9',
  'state.cameraBlocked.message': 'Pour utiliser l\u2019essayage virtuel, autorisez l\u2019acc\u00e8s \u00e0 la cam\u00e9ra dans les param\u00e8tres de votre navigateur et rechargez la page.',

  'state.cameraDenied.title': 'Acc\u00e8s cam\u00e9ra refus\u00e9',
  'state.cameraDenied.message': 'L\u2019essayage virtuel n\u00e9cessite l\u2019acc\u00e8s \u00e0 la cam\u00e9ra. Accordez la permission dans votre navigateur et r\u00e9essayez.',
  'state.cameraDenied.retry': 'R\u00e9essayer',

  'state.cameraError.title': 'Erreur cam\u00e9ra',

  'state.startingCamera.title': 'D\u00e9marrage de la cam\u00e9ra\u2026',
  'state.startingCamera.message': 'Veuillez autoriser l\u2019acc\u00e8s \u00e0 la cam\u00e9ra lorsque vous y \u00eates invit\u00e9.',

  'product.catalog': 'Catalogue de produits',

  'cart.addToCart': 'Ajouter au panier',
  'cart.added': 'Ajout\u00e9\u00a0!',
};

const es: Messages = {
  'modal.close': 'Cerrar prueba',
  'modal.switchCamera': 'Cambiar c\u00e1mara',
  'modal.snapshot': 'Tomar captura',
  'modal.ariaLabel': 'Prueba virtual de gafas',

  'tracking.alignFace': 'Alinea tu cara en el encuadre',
  'tracking.detected': 'Cara detectada',
  'tracking.lost': 'Cara no detectada \u2014 mira a la c\u00e1mara',
  'tracking.restoring': 'Restaurando\u2026',

  'mode.live': 'En vivo',
  'mode.photo': 'Foto',

  'photo.uploadTitle': 'Sube una foto o toma un sel\u00efi',
  'photo.uploadHint': 'JPG, PNG o WebP',

  'snapshot.download': 'Descargar',
  'snapshot.share': 'Compartir',
  'snapshot.copyLink': 'Copiar enlace',

  'state.cameraBlocked.title': 'Acceso a la c\u00e1mara bloqueado',
  'state.cameraBlocked.message': 'Para usar la prueba virtual, permite el acceso a la c\u00e1mara en la configuraci\u00f3n de tu navegador y recarga la p\u00e1gina.',

  'state.cameraDenied.title': 'Acceso a la c\u00e1mara denegado',
  'state.cameraDenied.message': 'La prueba virtual requiere acceso a la c\u00e1mara. Otorga el permiso en tu navegador e int\u00e9ntalo de nuevo.',
  'state.cameraDenied.retry': 'Reintentar',

  'state.cameraError.title': 'Error de c\u00e1mara',

  'state.startingCamera.title': 'Iniciando c\u00e1mara\u2026',
  'state.startingCamera.message': 'Por favor, permite el acceso a la c\u00e1mara cuando se te solicite.',

  'product.catalog': 'Cat\u00e1logo de productos',

  'cart.addToCart': 'A\u00f1adir al carrito',
  'cart.added': '\u00a1A\u00f1adido!',
};

const it: Messages = {
  'modal.close': 'Chiudi prova',
  'modal.switchCamera': 'Cambia fotocamera',
  'modal.snapshot': 'Scatta foto',
  'modal.ariaLabel': 'Prova virtuale occhiali',

  'tracking.alignFace': 'Allinea il tuo viso nel riquadro',
  'tracking.detected': 'Viso rilevato',
  'tracking.lost': 'Viso non rilevato \u2014 guarda la fotocamera',
  'tracking.restoring': 'Ripristino in corso\u2026',

  'mode.live': 'Live',
  'mode.photo': 'Foto',

  'photo.uploadTitle': 'Carica una foto o scatta un selfie',
  'photo.uploadHint': 'JPG, PNG o WebP',

  'snapshot.download': 'Scarica',
  'snapshot.share': 'Condividi',
  'snapshot.copyLink': 'Copia link',

  'state.cameraBlocked.title': 'Accesso fotocamera bloccato',
  'state.cameraBlocked.message': 'Per usare la prova virtuale, consenti l\u2019accesso alla fotocamera nelle impostazioni del browser e ricarica la pagina.',

  'state.cameraDenied.title': 'Accesso fotocamera negato',
  'state.cameraDenied.message': 'La prova virtuale richiede l\u2019accesso alla fotocamera. Concedi il permesso nel browser e riprova.',
  'state.cameraDenied.retry': 'Riprova',

  'state.cameraError.title': 'Errore fotocamera',

  'state.startingCamera.title': 'Avvio fotocamera\u2026',
  'state.startingCamera.message': 'Consenti l\u2019accesso alla fotocamera quando richiesto.',

  'product.catalog': 'Catalogo prodotti',

  'cart.addToCart': 'Aggiungi al carrello',
  'cart.added': 'Aggiunto!',
};

const pt: Messages = {
  'modal.close': 'Fechar prova',
  'modal.switchCamera': 'Trocar c\u00e2mera',
  'modal.snapshot': 'Tirar foto',
  'modal.ariaLabel': 'Prova virtual de \u00f3culos',

  'tracking.alignFace': 'Alinhe seu rosto no quadro',
  'tracking.detected': 'Rosto detectado',
  'tracking.lost': 'Rosto n\u00e3o detectado \u2014 olhe para a c\u00e2mera',
  'tracking.restoring': 'A restaurar\u2026',

  'mode.live': 'Ao vivo',
  'mode.photo': 'Foto',

  'photo.uploadTitle': 'Envie uma foto ou tire uma selfie',
  'photo.uploadHint': 'JPG, PNG ou WebP',

  'snapshot.download': 'Descarregar',
  'snapshot.share': 'Partilhar',
  'snapshot.copyLink': 'Copiar link',

  'state.cameraBlocked.title': 'Acesso \u00e0 c\u00e2mera bloqueado',
  'state.cameraBlocked.message': 'Para usar a prova virtual, permita o acesso \u00e0 c\u00e2mera nas defini\u00e7\u00f5es do browser e recarregue a p\u00e1gina.',

  'state.cameraDenied.title': 'Acesso \u00e0 c\u00e2mera negado',
  'state.cameraDenied.message': 'A prova virtual requer acesso \u00e0 c\u00e2mera. Conceda a permiss\u00e3o no browser e tente novamente.',
  'state.cameraDenied.retry': 'Tentar novamente',

  'state.cameraError.title': 'Erro de c\u00e2mera',

  'state.startingCamera.title': 'A iniciar c\u00e2mera\u2026',
  'state.startingCamera.message': 'Por favor, permita o acesso \u00e0 c\u00e2mera quando solicitado.',

  'product.catalog': 'Cat\u00e1logo de produtos',

  'cart.addToCart': 'Adicionar ao carrinho',
  'cart.added': 'Adicionado!',
};

const nl: Messages = {
  'modal.close': 'Passen sluiten',
  'modal.switchCamera': 'Camera wisselen',
  'modal.snapshot': 'Foto maken',
  'modal.ariaLabel': 'Virtueel brillen passen',

  'tracking.alignFace': 'Lijn uw gezicht uit in het kader',
  'tracking.detected': 'Gezicht gedetecteerd',
  'tracking.lost': 'Gezicht niet gedetecteerd \u2014 kijk naar de camera',
  'tracking.restoring': 'Herstellen\u2026',

  'mode.live': 'Live',
  'mode.photo': 'Foto',

  'photo.uploadTitle': 'Upload een foto of neem een selfie',
  'photo.uploadHint': 'JPG, PNG of WebP',

  'snapshot.download': 'Downloaden',
  'snapshot.share': 'Delen',
  'snapshot.copyLink': 'Link kopi\u00ebren',

  'state.cameraBlocked.title': 'Cameratoegang geblokkeerd',
  'state.cameraBlocked.message': 'Om virtueel te passen, sta cameratoegang toe in uw browserinstellingen en laad de pagina opnieuw.',

  'state.cameraDenied.title': 'Cameratoegang geweigerd',
  'state.cameraDenied.message': 'Virtueel passen vereist cameratoegang. Verleen toestemming in uw browser en probeer het opnieuw.',
  'state.cameraDenied.retry': 'Opnieuw proberen',

  'state.cameraError.title': 'Cameafout',

  'state.startingCamera.title': 'Camera starten\u2026',
  'state.startingCamera.message': 'Sta cameratoegang toe wanneer daarom wordt gevraagd.',

  'product.catalog': 'Productcatalogus',

  'cart.addToCart': 'In winkelwagen',
  'cart.added': 'Toegevoegd!',
};

const ro: Messages = {
  'modal.close': '\u00cenchi\u0219i proba',
  'modal.switchCamera': 'Schimb\u0103 camera',
  'modal.snapshot': 'Faceaz\u0103 o captare',
  'modal.ariaLabel': 'Prob\u0103 virtual\u0103 de ochelari',

  'tracking.alignFace': 'Alinia\u021bi fa\u021ba \u00een cadru',
  'tracking.detected': 'Fa\u021b\u0103 detectat\u0103',
  'tracking.lost': 'Fa\u021b\u0103 nedetectat\u0103 \u2014 prive\u0219te spre camer\u0103',
  'tracking.restoring': 'Se restaureaz\u0103\u2026',

  'mode.live': 'Live',
  'mode.photo': 'Foto',

  'photo.uploadTitle': '\u00eenc\u0103rca\u021bi o fotografie sau face\u021bi un selfie',
  'photo.uploadHint': 'JPG, PNG sau WebP',

  'snapshot.download': 'Descarca',
  'snapshot.share': 'Distribuie',
  'snapshot.copyLink': 'Copiaz\u0103 link',

  'state.cameraBlocked.title': 'Accesul la camer\u0103 blocat',
  'state.cameraBlocked.message': 'Pentru a folosi proba virtual\u0103, permite\u021bi accesul la camer\u0103 \u00een set\u0103rile browserului \u0219i re\u00eenc\u0103rca\u021bi pagina.',

  'state.cameraDenied.title': 'Accesul la camer\u0103 refuzat',
  'state.cameraDenied.message': 'Proba virtual\u0103 necesit\u0103 accesul la camer\u0103. Acorda\u021bi permisiunea \u00een browser \u0219i \u00eencerca\u021bi din nou.',
  'state.cameraDenied.retry': '\u00cencerca\u021bi din nou',

  'state.cameraError.title': 'Eroare camer\u0103',

  'state.startingCamera.title': 'Se porne\u0219te camera\u2026',
  'state.startingCamera.message': 'V\u0103 rug\u0103m s\u0103 permite\u021bi accesul la camer\u0103 c\u00e2nd vi se solicit\u0103.',

  'product.catalog': 'Catalog produse',

  'cart.addToCart': 'Ad\u0103uga\u021bi \u00een co\u0219',
  'cart.added': 'Ad\u0103ugat!',
};

const LOCALES: Record<Locale, Messages> = { en, de, fr, es, it, pt, nl, ro };

/**
 * Translate a key to the given locale, falling back to English if the locale
 * is not supported or the key is missing.
 *
 * @param locale - BCP 47 locale string (or prefix, e.g. "en-US" → "en").
 * @param key    - One of the defined Messages keys.
 * @returns The translated string.
 */
export function t(locale: string, key: keyof Messages): string {
  // Normalize: take the first two characters (language subtag only)
  const lang = locale.slice(0, 2) as Locale;
  const messages = LOCALES[lang] ?? en;
  return messages[key] ?? en[key];
}
